/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, tokenManager } from '../utils/api';
import { logger } from '../utils/logger';

// Token refresh interval - refresh 5 minutes before expiry (60 min token - 5 min buffer = 55 min)
const TOKEN_REFRESH_INTERVAL = 55 * 60 * 1000;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Initialize auth state from localStorage
     */
    useEffect(() => {
        const initializeAuth = () => {
            try {
                const hasTokens = tokenManager.hasTokens();
                const storedUser = tokenManager.getUser();
                const storedTenant = tokenManager.getTenant();

                if (hasTokens && storedUser) {
                    setUser(storedUser);
                    setTenant(storedTenant);
                    setIsAuthenticated(true);
                }
            } catch (error) {
                logger.error('Error initializing auth:', error);
                tokenManager.clearTokens();
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, []);

    /**
     * Update user activity periodically and proactively refresh tokens
     */
    const tokenRefreshIntervalRef = useRef(null);
    const activityIntervalRef = useRef(null);
    const lastRefreshAttemptRef = useRef(0);
    const isRefreshingRef = useRef(false);

    // Debounce interval for token refresh on focus/visibility (prevent rapid repeated calls)
    const REFRESH_DEBOUNCE_MS = 5000;

    useEffect(() => {
        if (isAuthenticated) {
            // Update activity immediately
            authAPI.updateActivity();

            // Update activity every 5 minutes
            activityIntervalRef.current = setInterval(() => {
                authAPI.updateActivity();
            }, 5 * 60 * 1000);

            // Proactive token refresh - refresh 5 minutes before expiry
            // This prevents 401 errors after periods of inactivity
            const refreshAccessToken = async (force = false) => {
                // Prevent concurrent refresh attempts
                if (isRefreshingRef.current) {
                    logger.log('[AuthContext] Token refresh already in progress, skipping...');
                    return;
                }

                // Debounce rapid refresh attempts (unless forced)
                const now = Date.now();
                if (!force && (now - lastRefreshAttemptRef.current) < REFRESH_DEBOUNCE_MS) {
                    logger.log('[AuthContext] Token refresh debounced, skipping...');
                    return;
                }

                try {
                    isRefreshingRef.current = true;
                    lastRefreshAttemptRef.current = now;

                    const refreshToken = tokenManager.getRefreshToken();
                    if (!refreshToken) {
                        logger.warn('[AuthContext] No refresh token available');
                        return;
                    }

                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/auth/refresh`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ refresh_token: refreshToken }),
                    });

                    if (response.ok) {
                        const data = await response.json();
                        tokenManager.setTokens(data.access_token, data.refresh_token);
                        logger.log('[AuthContext] Token refreshed successfully');
                    } else if (response.status === 401) {
                        // Refresh token is invalid/expired - user needs to re-login
                        logger.warn('[AuthContext] Refresh token expired, redirecting to login...');
                        tokenManager.clearTokens();
                        setUser(null);
                        setTenant(null);
                        setIsAuthenticated(false);
                        // Redirect to login
                        window.location.href = '/login';
                    } else {
                        logger.warn('[AuthContext] Token refresh failed with status:', response.status);
                    }
                } catch (error) {
                    logger.error('[AuthContext] Token refresh error:', error);
                } finally {
                    isRefreshingRef.current = false;
                }
            };

            // Refresh token 5 minutes before expiry (at 55 min mark for 60 min token)
            tokenRefreshIntervalRef.current = setInterval(() => refreshAccessToken(true), TOKEN_REFRESH_INTERVAL);

            // CRITICAL: Handle page visibility change - refresh token immediately when user returns
            // Browser throttles/suspends timers when tab is inactive, so we need to check on return
            const handleVisibilityChange = () => {
                if (document.visibilityState === 'visible') {
                    logger.log('[AuthContext] Page became visible, checking token...');
                    refreshAccessToken();
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);

            // Also refresh on window focus (handles alt-tab scenarios)
            const handleFocus = () => {
                logger.log('[AuthContext] Window focused, checking token...');
                refreshAccessToken();
            };

            window.addEventListener('focus', handleFocus);

            return () => {
                if (activityIntervalRef.current) clearInterval(activityIntervalRef.current);
                if (tokenRefreshIntervalRef.current) clearInterval(tokenRefreshIntervalRef.current);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                window.removeEventListener('focus', handleFocus);
            };
        }
    }, [isAuthenticated]);

    /**
     * Login user (using email and password)
     */
    const login = useCallback(async (email, password) => {
        try {
            setIsLoading(true);
            const { user, tenant } = await authAPI.login(email, password);

            setUser(user);
            setTenant(tenant);
            setIsAuthenticated(true);

            return { success: true };
        } catch (error) {
            logger.error('Login failed:', error);

            let message = 'Invalid email or password';
            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (typeof detail === 'string') {
                    message = detail;
                } else if (detail.message) {
                    message = detail.message;
                } else if (detail.error) {
                    message = detail.error === 'invalid_credentials' ? 'Invalid email or password' : detail.error;
                }
            } else if (error.response?.status === 401) {
                message = 'Invalid email or password';
            }

            return { success: false, error: message };
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Signup new user (email is used for login) - Creates new tenant
     */
    const signup = useCallback(async (email, password, name, companyName) => {
        try {
            setIsLoading(true);
            const result = await authAPI.signup(email, password, name, companyName);

            return { success: true, data: result.data };
        } catch (error) {
            logger.error('Signup failed:', error);

            let message = 'Signup failed';
            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (typeof detail === 'string') {
                    message = detail;
                } else if (detail.message) {
                    message = detail.message;
                }
            }

            return { success: false, error: message };
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Logout user
     */
    const logout = useCallback(async () => {
        try {
            await authAPI.logout();
        } catch (error) {
            logger.error('Logout error:', error);
        } finally {
            setUser(null);
            setTenant(null);
            setIsAuthenticated(false);
            tokenManager.clearTokens();
        }
    }, []);

    /**
     * Refresh user data
     */
    const refreshUser = useCallback(async () => {
        try {
            const userData = await authAPI.getCurrentUser();
            setUser(userData);
            tokenManager.setUser(userData);
        } catch (error) {
            logger.error('Refresh user error:', error);
            // If refresh fails, logout
            logout();
        }
    }, [logout]);

    /**
     * Change password
     */
    const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
        try {
            await authAPI.changePassword(currentPassword, newPassword, confirmPassword);
            return { success: true, message: 'Password changed successfully' };
        } catch (error) {
            logger.error('Change password error:', error);

            let message = 'Failed to change password';
            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (typeof detail === 'string') {
                    message = detail;
                } else if (detail.message) {
                    message = detail.message;
                }
            }

            return { success: false, error: message };
        }
    }, []);

    const value = {
        user,
        tenant,
        isAuthenticated,
        isLoading,
        login,
        signup,
        logout,
        refreshUser,
        changePassword,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
